import { CurrentUserCheck } from './auth0/types';

/**
 * Represents a generic Authenticator.
 * @template C Configuration types
 */
export abstract class Authenticator<C> {
  private lastSessionActivity: number | null;

  constructor(private config?: C) {
    this.lastSessionActivity = null;
  }

  /**
   * Sets the configuration
   * @param {C} config
   */
  public setConfig(config: C): void {
    this.config = config;
  }

  /**
   * Gets the configuration.
   * @returns {C} configuration
   * @throws
   */
  public getConfig(): C | never {
    if (!this.config) {
      throw new Error('There is no configuration set, please call "setConfig" method first');
    }

    return this.config;
  }

  /**
   * Gets the last time activity was recorded on the current auth session -- or
   * null if there is no active session.
   *
   * The intended use-case for this is to manually end a session after a period
   * of inactivity.
   *
   * @returns The Unix Ms timestamp of last activity (null if no active session)
   */
  public getLastSessionActivity(): number | null {
    return this.lastSessionActivity;
  }

  /**
   * Requests new credentials from the server.
   * @returns {Promise<unknown>}
   * @throws
   */
  public abstract fetchCredentials(): Promise<unknown>;

  /**
   * Checks if the credentials have expired.
   * @returns {Promise<boolean>}
   * @throws
   */
  public abstract hasExpiredCredentials(): Promise<boolean | never>;

  public abstract changePassword(user: unknown, oldPasswordcode: string, newPassword: string): Promise<unknown>;

  public abstract completeNewPassword(user: unknown, password: string, requiredAttributes?: unknown): Promise<unknown>;

  public abstract confirmSignIn(user: unknown, code: string, mfaType: unknown): Promise<unknown>;

  public abstract confirmSignUp(username: string, code: string, options?: unknown): Promise<unknown>;

  public abstract getCurrentAuthenticatedUser(currentUser?: CurrentUserCheck): Promise<unknown>;

  public abstract federatedSignIn(options: unknown): Promise<unknown>;

  public abstract forgotPassword(username: string): Promise<unknown>;

  public abstract forgotPasswordSubmit(username: string, code: string, password: string): Promise<unknown>;

  public abstract getPreferredMFA(user: unknown): Promise<unknown>;

  public abstract getUserAttributes(user: unknown): Promise<unknown>;

  public abstract resendSignUp(username: string): Promise<unknown>;

  public abstract setPreferredMFA(user: unknown, mfaMethod: string): Promise<unknown>;

  public abstract setupTOTP(user: unknown): Promise<unknown>;

  public abstract signIn(username?: string, password?: string): Promise<unknown>;

  public abstract signOut(showError?: boolean): Promise<unknown>;

  public abstract signUp(data: unknown, ...restOfAttrs: unknown[]): Promise<unknown>;

  public abstract updateUserAttributes(user: unknown, attributes: unknown): Promise<unknown>;

  public abstract verifyTotpToken(user: unknown, challenge: unknown): Promise<unknown>;

  public abstract verifyCurrentUserAttributeSubmit(attribute: unknown, code: unknown): Promise<unknown>;

  public abstract verifyUserAttribute(user: unknown, attr: unknown): Promise<unknown>;

  public abstract handleRedirectCallback(): Promise<void>;

  public abstract updateCurrentUser(currentUser?: CurrentUserCheck): void;

  /**
   * Record that there has been some activity on the current users' session.
   */
  protected recordSessionActivity(): void {
    this.lastSessionActivity = Date.now();
  }

  /**
   * Record that the current users' session has ended.
   */
  protected recordSessionEnded(): void {
    this.lastSessionActivity = null;
  }
}
